import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Package, RefreshCw, LogOut, User as UserIcon, Calendar, ShieldCheck, ExternalLink, Mail, Loader2, AlertCircle, CheckCircle2, Truck, XCircle, Download, FileText, Gift } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useCustomerAuth } from '@/context/CustomerAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_BADGE = {
  pending:        { label: 'Pending payment', cls: 'bg-amber-100 text-amber-800' },
  paid:           { label: 'Paid',            cls: 'bg-emerald-100 text-emerald-800' },
  accepted:       { label: 'Accepted',        cls: 'bg-emerald-100 text-emerald-800' },
  shipped:        { label: 'Shipped',         cls: 'bg-sky-100 text-sky-800' },
  rejected:       { label: 'Rejected',        cls: 'bg-red-100 text-red-700' },
  quote_pending:  { label: 'Quote pending',   cls: 'bg-amber-100 text-amber-800' },
  active:         { label: 'Active',          cls: 'bg-emerald-100 text-emerald-800' },
  cancelled:      { label: 'Cancelled',       cls: 'bg-slate-100 text-slate-700' },
  past_due:       { label: 'Past due',        cls: 'bg-red-100 text-red-700' },
  expired:        { label: 'Expired',         cls: 'bg-slate-100 text-slate-700' },
};

function fmtDate(iso) {
  if (!iso) return '\u2014';
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium' }); } catch { return iso.slice(0, 10); }
}

export default function Account() {
  const { customer, loading, logout } = useCustomerAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [tab, setTab] = useState('orders');
  const [busy, setBusy] = useState(true);
  const [portalBusy, setPortalBusy] = useState('');
  const [cancelBusy, setCancelBusy] = useState('');
  const [pauseBusy, setPauseBusy] = useState('');
  const [saveOfferSub, setSaveOfferSub] = useState(null);
  const [invoiceBusy, setInvoiceBusy] = useState('');
  const [invoiceSent, setInvoiceSent] = useState({}); // map of order_id -> true once emailed
  const [verifBusy, setVerifBusy] = useState(false);
  const [verifMsg, setVerifMsg] = useState('');

  const fetchData = useCallback(async () => {
    setBusy(true);
    try {
      const [oRes, sRes] = await Promise.all([
        axios.get(`${API}/customers/orders`, { withCredentials: true }),
        axios.get(`${API}/customers/subscriptions`, { withCredentials: true }),
      ]);
      setOrders(oRes.data || []);
      setSubs(sRes.data || []);
    } catch (e) {
      if (e.response?.status === 401) navigate('/login?next=/account', { replace: true });
    } finally {
      setBusy(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (loading) return;
    if (!customer || customer === false) { navigate('/login?next=/account', { replace: true }); return; }
    fetchData();
  }, [customer, loading, fetchData, navigate]);

  const handleManage = async (sub) => {
    setPortalBusy(sub.id);
    try {
      const r = await axios.post(`${API}/customers/subscriptions/${sub.id}/portal`, {
        return_url: window.location.origin + '/account',
      }, { withCredentials: true });
      if (r.data?.portal_url) window.location.href = r.data.portal_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open the management portal.');
    } finally {
      setPortalBusy('');
    }
  };

  const handleCancel = async (sub) => {
    setCancelBusy(sub.id);
    try {
      const r = await axios.post(`${API}/customers/subscriptions/${sub.id}/cancel`, {}, { withCredentials: true });
      toast.success(r.data?.message || 'Your Auto-Ship has been cancelled.');
      setSaveOfferSub(null);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not cancel the subscription.');
    } finally {
      setCancelBusy('');
    }
  };

  const handlePause = async (sub) => {
    setPauseBusy(sub.id);
    try {
      const r = await axios.post(`${API}/customers/subscriptions/${sub.id}/pause`, {}, { withCredentials: true });
      toast.success(r.data?.message || 'Your Auto-Ship is paused for a month.');
      setSaveOfferSub(null);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not pause the subscription.');
    } finally {
      setPauseBusy('');
    }
  };

  const handleResume = async (sub) => {
    setPauseBusy(sub.id);
    try {
      const r = await axios.post(`${API}/customers/subscriptions/${sub.id}/resume`, {}, { withCredentials: true });
      toast.success(r.data?.message || 'Your Auto-Ship has resumed.');
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not resume the subscription.');
    } finally {
      setPauseBusy('');
    }
  };

  const handleResendVerification = async () => {
    setVerifBusy(true);
    setVerifMsg('');
    try {
      const r = await axios.post(`${API}/customers/auth/send-verification`, {}, { withCredentials: true });
      setVerifMsg(r.data?.message || 'Verification email sent.');
    } catch (e) {
      setVerifMsg(e.response?.data?.detail || 'Could not send verification email.');
    } finally {
      setVerifBusy(false);
    }
  };

  const handleDownloadInvoice = async (order) => {
    const key = `dl-${order.id || order.invoice_number}`;
    setInvoiceBusy(key);
    try {
      const r = await axios.get(
        `${API}/customers/orders/${order.id}/invoice/download`,
        { withCredentials: true, responseType: 'blob' },
      );
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice-${order.invoice_number || order.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not download the invoice.');
    } finally {
      setInvoiceBusy('');
    }
  };

  const handleEmailInvoice = async (order) => {
    const key = `em-${order.id || order.invoice_number}`;
    setInvoiceBusy(key);
    try {
      const r = await axios.post(
        `${API}/customers/orders/${order.id}/invoice/email`,
        {},
        { withCredentials: true },
      );
      if (r.data?.ok) {
        setInvoiceSent((m) => ({ ...m, [order.id]: true }));
        toast.success(`Invoice sent to ${r.data.email}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not email the invoice.');
    } finally {
      setInvoiceBusy('');
    }
  };

  if (loading || !customer || customer === false) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="account-loading">
        <Loader2 className="w-7 h-7 animate-spin text-emerald-700" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-160px)] py-12 px-4" style={{ backgroundColor: '#f7f9f3' }} data-testid="account-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-sm border border-emerald-100 p-6 mb-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              {customer.picture ? (
                <img loading="lazy" decoding="async" src={customer.picture} alt={customer.name || customer.email} className="w-14 h-14 rounded-full object-cover border-2 border-emerald-100" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
                  <UserIcon className="w-7 h-7 text-emerald-700" />
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">My Account</div>
                <div className="text-xl font-bold text-slate-800" data-testid="account-name">{customer.name || customer.email}</div>
                <div className="text-sm text-slate-600 flex items-center gap-1.5"><Mail className="w-3 h-3" /> {customer.email}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={fetchData} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm" data-testid="account-refresh"><RefreshCw className="w-4 h-4" /> Refresh</button>
              <button onClick={async () => { await logout(); navigate('/'); }} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 text-red-700 hover:bg-red-50 text-sm" data-testid="account-logout"><LogOut className="w-4 h-4" /> Sign out</button>
            </div>
          </div>
        </motion.div>

        {/* Email-verification banner (only shown for password-signup users who haven't verified yet) */}
        {!customer.email_verified && customer.auth_provider !== 'google' && (
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3 flex-wrap" data-testid="account-unverified-banner">
            <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-[200px]">
              <p className="text-sm font-semibold text-amber-900">Confirm your email</p>
              <p className="text-xs text-amber-800 mt-0.5">We sent a verification link to <b>{customer.email}</b>. Please confirm it so we can deliver order updates and shipping alerts to your inbox.</p>
              {verifMsg && <p className="text-xs text-emerald-800 mt-1.5 font-medium" data-testid="account-verif-msg">{verifMsg}</p>}
            </div>
            <button onClick={handleResendVerification} disabled={verifBusy} data-testid="account-resend-verification" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-700 hover:bg-amber-800 disabled:opacity-60 text-white text-xs font-medium">
              {verifBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
              {verifBusy ? 'Sending\u2026' : 'Resend email'}
            </button>
          </motion.div>
        )}

        {/* IIY Rewards */}
        {(() => {
          const bal = Number(customer.points_balance || 0);
          const pct = Math.min(100, Math.round((bal % 500) / 500 * 100));
          const toNext = bal >= 500 ? 0 : 500 - bal;
          return (
            <div className="rounded-2xl p-5 mb-5 bg-gradient-to-br from-emerald-700 to-emerald-900 text-white shadow-lg" data-testid="account-rewards-card">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center"><Gift className="w-6 h-6" /></div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-emerald-200 font-semibold">IIY Rewards</div>
                    <div className="text-2xl font-bold" data-testid="account-points-balance">{bal.toLocaleString()} points</div>
                  </div>
                </div>
                <div className="text-right">
                  {bal >= 500
                    ? <span className="inline-block bg-amber-400 text-emerald-950 text-sm font-bold px-3 py-1.5 rounded-lg" data-testid="account-reward-ready">£5 reward ready at checkout 🎉</span>
                    : <span className="text-sm text-emerald-100">{toNext} points to your next <b>£5 reward</b></span>}
                </div>
              </div>
              <div className="mt-4 h-2.5 rounded-full bg-white/20 overflow-hidden">
                <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${bal >= 500 ? 100 : pct}%` }} />
              </div>
              <p className="text-[11px] text-emerald-200 mt-2">Earn 2 points for every £1 (ex-VAT) spent. Redeem 500 points for £5 off your next order.</p>
            </div>
          );
        })()}

        {/* Tabs */}
        <div className="flex gap-2 mb-5">
          <button onClick={() => setTab('orders')} data-testid="account-tab-orders" className={`px-5 py-2.5 rounded-xl font-medium transition ${tab === 'orders' ? 'bg-emerald-700 text-white' : 'bg-white border border-slate-200 text-slate-700'}`}><Package className="w-4 h-4 inline mr-1.5" />Orders ({orders.length})</button>
          <button onClick={() => setTab('subscriptions')} data-testid="account-tab-subs" className={`px-5 py-2.5 rounded-xl font-medium transition ${tab === 'subscriptions' ? 'bg-emerald-700 text-white' : 'bg-white border border-slate-200 text-slate-700'}`}><Calendar className="w-4 h-4 inline mr-1.5" />Subscriptions ({subs.length})</button>
        </div>

        {busy ? (
          <div className="text-center py-16"><Loader2 className="w-7 h-7 animate-spin text-emerald-700 mx-auto" /></div>
        ) : tab === 'orders' ? (
          <div className="space-y-3" data-testid="account-orders-list">
            {orders.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-slate-200">
                <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-700 font-medium">No orders yet</p>
                <p className="text-sm text-slate-500 mt-1">Orders placed with this email will appear here automatically.</p>
                <Link to="/products" className="inline-block mt-4 px-5 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white text-sm">Shop now</Link>
              </div>
            ) : orders.map((o) => {
              const badge = STATUS_BADGE[o.status] || { label: o.status, cls: 'bg-slate-100 text-slate-700' };
              const isPaid = o.payment_status === 'paid' || ['paid', 'accepted', 'shipped'].includes(o.status);
              const dlKey = `dl-${o.id || o.invoice_number}`;
              const emKey = `em-${o.id || o.invoice_number}`;
              const sent = !!invoiceSent[o.id];
              return (
                <motion.div key={o.id || o.invoice_number} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`account-order-${o.invoice_number || o.id}`}>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-mono font-semibold text-slate-800">{o.invoice_number || (o.id || '').slice(0, 8)}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.cls}`}>{badge.label}</span>
                      </div>
                      <div className="text-sm text-slate-600">{o.quantity} bottle{(o.quantity ?? 0) > 1 ? 's' : ''} · {fmtDate(o.created_at)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-emerald-700">£{(o.total || 0).toFixed(2)}</div>
                      {o.tracking_number && (
                        <a href={o.tracking_url || `https://www.royalmail.com/track-your-item#/tracking-results/${o.tracking_number}`} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-700 hover:underline flex items-center gap-1 mt-1"><Truck className="w-3 h-3" />{o.tracking_number}</a>
                      )}
                    </div>
                    {o.invoice_number && (
                      <Link to={`/track-order?inv=${encodeURIComponent(o.invoice_number)}`} className="text-xs px-3 py-2 rounded-lg border border-slate-300 hover:bg-slate-50">Track</Link>
                    )}
                  </div>
                  {isPaid && (
                    <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-2">
                      <button
                        onClick={() => handleDownloadInvoice(o)}
                        disabled={invoiceBusy === dlKey}
                        data-testid={`account-order-download-${o.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-700 text-emerald-700 hover:bg-emerald-50 disabled:opacity-60 text-xs font-medium"
                      >
                        {invoiceBusy === dlKey ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        Download invoice
                      </button>
                      <button
                        onClick={() => handleEmailInvoice(o)}
                        disabled={invoiceBusy === emKey || sent}
                        data-testid={`account-order-email-${o.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-60 text-xs font-medium"
                      >
                        {invoiceBusy === emKey ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : sent ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <FileText className="w-3.5 h-3.5" />}
                        {sent ? 'Sent to your inbox' : 'Email me invoice'}
                      </button>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-3" data-testid="account-subs-list">
            {subs.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-slate-200">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-700 font-medium">No subscriptions yet</p>
                <p className="text-sm text-slate-500 mt-1">Save 31.10% with monthly auto-ship of BCM-95®.</p>
                <Link to="/subscribe" className="inline-block mt-4 px-5 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white text-sm">Subscribe & Save</Link>
              </div>
            ) : subs.map((s) => {
              const badge = STATUS_BADGE[s.status] || { label: s.status, cls: 'bg-slate-100 text-slate-700' };
              const StatusIcon = s.status === 'active' ? CheckCircle2 : s.status === 'cancelled' ? XCircle : AlertCircle;
              return (
                <motion.div key={s.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`account-sub-${s.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[220px]">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <StatusIcon className={`w-4 h-4 ${s.status === 'active' ? 'text-emerald-600' : s.status === 'cancelled' ? 'text-slate-400' : 'text-amber-600'}`} />
                        <span className="font-semibold text-slate-800">BCM-95® Monthly Auto-Ship</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.cls}`}>{badge.label}</span>
                      </div>
                      <div className="text-sm text-slate-600">£{(s.amount_per_period || 0).toFixed(2)} / {s.interval} · started {fmtDate(s.created_at)}</div>
                      {s.last_invoice_paid_at && <div className="text-xs text-slate-500 mt-0.5">Last charge: {fmtDate(s.last_invoice_paid_at)}</div>}
                      {s.paused_until && s.status !== 'cancelled' && (
                        <div className="text-xs text-sky-700 font-medium mt-0.5" data-testid={`sub-paused-notice-${s.id}`}>Paused · resumes {fmtDate(s.paused_until)}</div>
                      )}
                      {s.cancel_at_period_end && s.status !== 'cancelled' && (
                        <div className="text-xs text-amber-700 font-medium mt-0.5" data-testid={`sub-cancel-notice-${s.id}`}>Cancels on {fmtDate(s.cancel_effective_at)} · no further payments</div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 items-end">
                      {s.status === 'active' && s.stripe_customer_id ? (
                        <>
                          <button onClick={() => handleManage(s)} disabled={portalBusy === s.id} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium disabled:opacity-60" data-testid={`manage-sub-${s.id}`}>
                            {portalBusy === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                            Manage <ExternalLink className="w-3 h-3 opacity-60" />
                          </button>
                          {s.paused_until && !s.cancel_at_period_end && (
                            <button onClick={() => handleResume(s)} disabled={pauseBusy === s.id} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium disabled:opacity-60" data-testid={`resume-sub-${s.id}`}>
                              {pauseBusy === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                              Resume now
                            </button>
                          )}
                          {!s.cancel_at_period_end && !s.paused_until && (
                            <button onClick={() => setSaveOfferSub(s)} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white border border-red-300 text-red-700 hover:bg-red-50 text-sm font-medium" data-testid={`cancel-sub-${s.id}`}>
                              <XCircle className="w-4 h-4" />
                              Cancel
                            </button>
                          )}
                        </>
                      ) : s.status === 'pending' ? (
                        <span className="text-xs text-amber-700">Awaiting payment confirmation</span>
                      ) : null}
                      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-mono">{s.id?.slice(0, 8)}…</span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
            <p className="text-xs text-slate-500 text-center mt-4">Auto-Ship has a 3-month minimum (one quarter) — after that you can cancel any time with the Cancel button above. Update your card or address from the Stripe-hosted portal via Manage. Need help? <a href="mailto:support@iiypv.com" className="text-emerald-700 hover:underline">support@iiypv.com</a></p>
          </div>
        )}
      </div>

      {saveOfferSub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" data-testid="save-offer-modal" onClick={() => setSaveOfferSub(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-3">
              <span className="flex-shrink-0 w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center"><RefreshCw className="w-5 h-5 text-sky-700" /></span>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Before you go — pause instead?</h3>
                <p className="text-sm text-slate-600 mt-1">Need a break this month? Pause your Auto-Ship for a month with no charge — it resumes automatically, and you keep your discounted rate. No need to cancel.</p>
              </div>
            </div>
            <div className="flex flex-col gap-2 mt-5">
              <button onClick={() => handlePause(saveOfferSub)} disabled={pauseBusy === saveOfferSub.id} className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-sky-600 hover:bg-sky-700 text-white font-semibold disabled:opacity-60" data-testid="save-offer-pause-btn">
                {pauseBusy === saveOfferSub.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
                Pause for a month instead
              </button>
              <button onClick={() => handleCancel(saveOfferSub)} disabled={cancelBusy === saveOfferSub.id} className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-white border border-red-300 text-red-700 hover:bg-red-50 font-medium disabled:opacity-60" data-testid="save-offer-cancel-btn">
                {cancelBusy === saveOfferSub.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                No thanks, cancel my subscription
              </button>
              <button onClick={() => setSaveOfferSub(null)} className="w-full px-4 py-2 rounded-lg text-slate-500 hover:text-slate-700 text-sm" data-testid="save-offer-dismiss-btn">Keep my subscription</button>
            </div>
            <p className="text-[11px] text-slate-400 text-center mt-3">Cancelling stops future payments at the end of your current paid month (3-month minimum applies).</p>
          </div>
        </div>
      )}
    </div>
  );
}
